// src/components/configuracion/AutomatizacionTab.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Grid,
  Chip,
  Tooltip,
  IconButton,
  CircularProgress,
  Divider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import TextField from '@mui/material/TextField';
import {
  Save as SaveIcon,
  PlayArrow as PlayIcon,
  Schedule as ScheduleIcon,
  Refresh as RefreshIcon,
  InfoOutlined as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from '@mui/icons-material';
import { CardContainer } from '../shared';
import { empresaService } from '../../services/empresaService';
import { useNotification } from '../../hooks';

const AutomatizacionTab = () => {
  const { empresaActiva } = useSelector(state => state.empresa);
  const { showNotification } = useNotification();
  const rutActivo = empresaActiva?.rut;

  const [siiConfig, setSiiConfig] = useState({
    rut_rep_legal: '', clave_sii: '',
    scheduler_activo: false, scheduler_hora: 2,
  });
  const [siiConfigured, setSiiConfigured] = useState(false);
  const [loadingConfigSii, setLoadingConfigSii] = useState(false);
  const [showClaveSii, setShowClaveSii] = useState(false);
  const [siiJobs, setSiiJobs] = useState([]);
  const [ejecutandoDescarga, setEjecutandoDescarga] = useState(false);

  const lastLoadedRut = useRef(null);
  const pollingRef = useRef(null);

  const recargarJobs = useCallback(() => {
    if (!rutActivo) return;
    empresaService.getJobsSii(rutActivo)
      .then(data => setSiiJobs(data || []))
      .catch(() => {});
  }, [rutActivo]);

  useEffect(() => {
    if (!rutActivo || lastLoadedRut.current === rutActivo) return;
    lastLoadedRut.current = rutActivo;

    empresaService.getConfigSii(rutActivo)
      .then(data => {
        setSiiConfigured(data.configurada);
        if (data.configurada) {
          setSiiConfig(prev => ({
            ...prev,
            rut_rep_legal: data.rut_rep_legal,
            scheduler_activo: data.scheduler_activo ?? false,
            scheduler_hora: data.scheduler_hora ?? 2,
          }));
        }
      })
      .catch(err => console.error('Error al cargar config SII:', err));

    recargarJobs();
  }, [rutActivo, recargarJobs]);

  // Polling cuando hay job ejecutando
  useEffect(() => {
    const hayEjecutando = siiJobs.some(j => j.estado === 'ejecutando');
    if (hayEjecutando) {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(recargarJobs, 8000);
      }
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [siiJobs, recargarJobs]);

  const handleSiiConfigChange = useCallback((e) => {
    const { name, value } = e.target;
    setSiiConfig(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSiiConfigSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!siiConfig.rut_rep_legal) {
      showNotification('Debe ingresar el RUT del representante legal', 'error');
      return;
    }
    if (!siiConfigured && !siiConfig.clave_sii) {
      showNotification('Debe ingresar la clave SII para la primera configuración', 'error');
      return;
    }
    setLoadingConfigSii(true);
    try {
      await empresaService.upsertConfigSii({
        rut_empresa: rutActivo,
        rut_rep_legal: siiConfig.rut_rep_legal,
        clave_sii: siiConfig.clave_sii,
        scheduler_activo: siiConfig.scheduler_activo,
        scheduler_hora: Number(siiConfig.scheduler_hora),
        scheduler_dias_atras: 30,
      });
      setSiiConfigured(true);
      setSiiConfig(prev => ({ ...prev, clave_sii: '' }));
      showNotification('Configuración SII guardada correctamente', 'success');
    } catch (err) {
      showNotification(err?.response?.data?.detail || 'Error al guardar configuración SII', 'error');
    } finally {
      setLoadingConfigSii(false);
    }
  }, [siiConfig, rutActivo, siiConfigured, showNotification]);

  const handleEjecutarDescarga = useCallback(async () => {
    setEjecutandoDescarga(true);
    try {
      await empresaService.ejecutarDescargaSii(rutActivo);
      showNotification('Proceso iniciado en segundo plano', 'success');
      setTimeout(recargarJobs, 2000);
    } catch (err) {
      showNotification(err?.message || 'Error al iniciar el proceso', 'error');
    } finally {
      setEjecutandoDescarga(false);
    }
  }, [rutActivo, showNotification, recargarJobs]);

  if (!empresaActiva) {
    return (
      <Alert severity="info">
        Seleccione una empresa para configurar la automatización.
      </Alert>
    );
  }

  return (
    <CardContainer
      title="Automatización diaria RCV"
      subheader="Proceso automático: obtiene token SII con el certificado digital → consulta RCV → importa documentos nuevos → descarga PDFs"
      actionComponent={
        siiConfigured
          ? <Chip icon={<CheckCircleIcon />} label="Configurado" color="success" size="small" />
          : <Chip icon={<WarningIcon />} label="Sin configurar" color="warning" size="small" />
      }
    >
      <Box component="form" onSubmit={handleSiiConfigSubmit}>
        <Grid container spacing={2} sx={{ maxWidth: 500 }}>

          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <InfoIcon sx={{ fontSize: 14 }} />
              Credenciales del portal web del SII (para descarga de PDFs). La autenticación RCV usa el certificado digital de la empresa.
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="RUT Representante Legal"
              name="rut_rep_legal"
              value={siiConfig.rut_rep_legal}
              onChange={handleSiiConfigChange}
              fullWidth
              size="small"
              placeholder="Ej: 6822000-9"
              helperText="RUT del representante legal con acceso al portal SII"
              disabled={loadingConfigSii}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Clave Tributaria"
              name="clave_sii"
              type={showClaveSii ? 'text' : 'password'}
              value={siiConfig.clave_sii}
              onChange={handleSiiConfigChange}
              fullWidth
              size="small"
              placeholder={siiConfigured ? '••••••••  (vacío = no cambiar)' : 'Ingrese la clave tributaria'}
              helperText="Clave de acceso al portal web SII"
              disabled={loadingConfigSii}
              InputProps={{
                endAdornment: (
                  <IconButton onClick={() => setShowClaveSii(v => !v)} edge="end" size="small">
                    {showClaveSii ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={loadingConfigSii ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
              disabled={loadingConfigSii}
            >
              {loadingConfigSii ? 'Guardando...' : 'Guardar'}
            </Button>
          </Grid>

          {/* Programación */}
          <Grid item xs={12}>
            <Divider>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <ScheduleIcon sx={{ fontSize: 14 }} /> Programación
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!siiConfig.scheduler_activo}
                  onChange={e => handleSiiConfigChange({ target: { name: 'scheduler_activo', value: e.target.checked } })}
                  disabled={loadingConfigSii}
                />
              }
              label="Ejecutar proceso completo automáticamente cada día"
            />
          </Grid>

          {siiConfig.scheduler_activo && (
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Hora de ejecución</InputLabel>
                <Select
                  label="Hora de ejecución"
                  name="scheduler_hora"
                  value={siiConfig.scheduler_hora ?? 2}
                  onChange={handleSiiConfigChange}
                  disabled={loadingConfigSii}
                >
                  {Array.from({ length: 24 }, (_, h) => (
                    <MenuItem key={h} value={h}>{String(h).padStart(2, '0')}:00 hrs</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          {/* Ejecución manual */}
          <Grid item xs={12}>
            <Divider>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <PlayIcon sx={{ fontSize: 14 }} /> Ejecución manual
              </Typography>
            </Divider>
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                color="secondary"
                startIcon={ejecutandoDescarga ? <CircularProgress size={18} color="inherit" /> : <PlayIcon />}
                onClick={handleEjecutarDescarga}
                disabled={ejecutandoDescarga}
              >
                {ejecutandoDescarga ? 'Iniciando...' : 'Ejecutar ahora'}
              </Button>
              <Typography variant="caption" color="text.secondary">
                Consulta el RCV, importa documentos nuevos y descarga los PDFs correspondientes.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Historial de ejecuciones */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">
            Historial de ejecuciones
            {siiJobs?.some(j => j.estado === 'ejecutando') && (
              <CircularProgress size={12} sx={{ ml: 1 }} />
            )}
          </Typography>
          <Tooltip title="Refrescar">
            <IconButton size="small" onClick={recargarJobs}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {(!siiJobs || siiJobs.length === 0) ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            Sin ejecuciones registradas. Usa "Ejecutar ahora" para iniciar el proceso.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Inicio</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell align="right">Docs</TableCell>
                <TableCell align="right">PDFs OK</TableCell>
                <TableCell align="right">Errores</TableCell>
                <TableCell>Por</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {siiJobs.slice(0, 10).map(job => (
                <Tooltip
                  key={job.id}
                  title={job.detalle ? JSON.parse(job.detalle).join('\n') : ''}
                  placement="left"
                  arrow
                >
                  <TableRow
                    sx={{
                      bgcolor: job.estado === 'ejecutando' ? 'action.hover' : undefined,
                      cursor: job.detalle ? 'help' : 'default',
                    }}
                  >
                    <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                      {job.fecha_inicio ? new Date(job.fecha_inicio).toLocaleString('es-CL') : '-'}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={job.estado}
                        size="small"
                        color={
                          job.estado === 'completado' ? 'success' :
                          job.estado === 'ejecutando' ? 'info' :
                          job.estado === 'parcial' ? 'warning' : 'error'
                        }
                      />
                    </TableCell>
                    <TableCell align="right">{job.total ?? '-'}</TableCell>
                    <TableCell align="right">{job.exitosos ?? '-'}</TableCell>
                    <TableCell align="right" sx={{ color: job.errores > 0 ? 'error.main' : undefined }}>
                      {job.errores ?? '-'}
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{job.iniciado_por}</TableCell>
                  </TableRow>
                </Tooltip>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </CardContainer>
  );
};

export default AutomatizacionTab;
